import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const api = axios.create({
  baseURL: `${API_URL}/api`,
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add auth token to requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Handle auth errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('token');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// Auth APIs
export const authAPI = {
  login: (data) => api.post('/auth/login', data),
  register: (data) => api.post('/auth/register', data),
  me: () => api.get('/auth/me'),
};

// Dashboard APIs
export const dashboardAPI = {
  getSummary: () => api.get('/dashboard/summary'),
  getUpcoming: (days = 7) => api.get('/dashboard/upcoming', { params: { days } }),
  getAlerts: () => api.get('/alerts'),
  getCreditCardInvoices: () => api.get('/dashboard/credit-card-invoices'),
};

// Accounts Payable APIs
export const payablesAPI = {
  list: (params) => api.get('/payables', { params }),
  get: (id) => api.get(`/payables/${id}`),
  create: (data) => api.post('/payables', data),
  update: (id, data) => api.put(`/payables/${id}`, data),
  delete: (id) => api.delete(`/payables/${id}`),
  pay: (id, data) => api.post(`/payables/${id}/pay`, data),
  reverse: (id, data) => api.post(`/payables/${id}/reverse`, data),
  unreconcile: (id) => api.post(`/payables/${id}/unreconcile`),
};

// Accounts Receivable APIs
export const receivablesAPI = {
  list: (params) => api.get('/receivables', { params }),
  get: (id) => api.get(`/receivables/${id}`),
  create: (data) => api.post('/receivables', data),
  update: (id, data) => api.put(`/receivables/${id}`, data),
  delete: (id) => api.delete(`/receivables/${id}`),
  receive: (id, data) => api.post(`/receivables/${id}/receive`, data),
  reverse: (id, data) => api.post(`/receivables/${id}/reverse`, data),
  unreconcile: (id) => api.post(`/receivables/${id}/unreconcile`),
};

// Reversals (Estornos) API
export const reversalsAPI = {
  list: (params) => api.get('/reversals', { params }),
};

// Bank Accounts APIs
export const accountsAPI = {
  list: (params) => api.get('/accounts', { params }),
  get: (id) => api.get(`/accounts/${id}`),
  create: (data) => api.post('/accounts', data),
  update: (id, data) => api.put(`/accounts/${id}`, data),
  delete: (id) => api.delete(`/accounts/${id}`),
  getBalance: (id) => api.get(`/accounts/${id}/balance`),
};

// Categories APIs
export const categoriesAPI = {
  list: (params) => api.get('/categories', { params }),
  get: (id) => api.get(`/categories/${id}`),
  create: (data) => api.post('/categories', data),
  update: (id, data) => api.put(`/categories/${id}`, data),
  delete: (id) => api.delete(`/categories/${id}`),
};

// People (Clients/Suppliers) APIs - Legacy
export const peopleAPI = {
  list: (params) => api.get('/people', { params }),
  get: (id) => api.get(`/people/${id}`),
  create: (data) => api.post('/people', data),
  update: (id, data) => api.put(`/people/${id}`, data),
  delete: (id) => api.delete(`/people/${id}`),
};

// Entities (Clients/Suppliers) APIs - New
export const entitiesAPI = {
  list: (params) => api.get('/entities', { params }),
  get: (id) => api.get(`/entities/${id}`),
  create: (data) => api.post('/entities', data),
  update: (id, data) => api.put(`/entities/${id}`, data),
  delete: (id) => api.delete(`/entities/${id}`),
  searchCNPJ: (cnpj) => api.get(`/entities/search-cnpj/${cnpj}`),
  importCSV: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/entities/import-csv', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  getFinancialDefaults: (id) => api.get(`/entities/${id}/financial-defaults`),
  updateFinancialDefaults: (id, data) => api.put(`/entities/${id}/financial-defaults`, data),
};

// Extrato Financeiro (Financial Extract) APIs
export const extractAPI = {
  import: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/extract/import', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  listMovements: (params) => api.get('/extract/movements', { params }),
};

// Financial Statement CSV Import APIs
export const financialStatementAPI = {
  uploadCSV: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/financial-statement/upload-csv', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  process: (fileId, mapping) => api.post(`/financial-statement/process/${fileId}`, mapping),
  history: () => api.get('/financial-statement/history'),
  transactions: (params) => api.get('/financial-statement/transactions', { params }),
};

// Transactions APIs
export const transactionsAPI = {
  list: (params) => api.get('/transactions', { params }),
  get: (id) => api.get(`/transactions/${id}`),
  create: (data) => api.post('/transactions', data),
  update: (id, data) => api.put(`/transactions/${id}`, data),
  delete: (id) => api.delete(`/transactions/${id}`),
};

// Cost Centers APIs
export const costCentersAPI = {
  list: (params) => api.get('/cost-centers', { params }),
  get: (id) => api.get(`/cost-centers/${id}`),
  create: (data) => api.post('/cost-centers', data),
  update: (id, data) => api.put(`/cost-centers/${id}`, data),
  delete: (id) => api.delete(`/cost-centers/${id}`),
};

// Payment Methods API
export const paymentMethodsAPI = {
  list: () => api.get('/payment-methods'),
  // Formas customizadas: as 8 padrao (pix, boleto...) vem do backend e nao
  // podem ser apagadas — o DELETE devolve 403 para elas e 409 se algum
  // lancamento ainda usa a forma.
  create: (data) => api.post('/payment-methods', data),
  delete: (id) => api.delete(`/payment-methods/${id}`),
};

// Cash Flow APIs
export const cashFlowAPI = {
  getData: (params) => api.get('/cash-flow', { params }),
  getUpcoming: (days = 30) => api.get('/cash-flow/upcoming', { params: { days } }),
  getBalanceProjection: (days = 90) => api.get('/cash-flow/balance-projection', { params: { days } }),
};

// Bank Reconciliation APIs
export const reconciliationAPI = {
  uploadOFX: (accountId, file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post(`/reconciliation/upload-ofx?account_id=${accountId}`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  getBankTransactions: (params) => api.get('/reconciliation/bank-transactions', { params }),
  getSuggestions: (bankTransactionId) => api.get('/reconciliation/suggestions', { 
    params: { bank_transaction_id: bankTransactionId } 
  }),
  reconcile: (data) => api.post('/reconciliation/reconcile', data),
  unreconcile: (bankTransactionId) => api.post(`/reconciliation/unreconcile/${bankTransactionId}`),
  getStats: (accountId) => api.get('/reconciliation/stats', { params: { account_id: accountId } }),
  getEfficiencyMetrics: (params) => api.get('/reconciliation/efficiency-metrics', { params }),
};

// Reports APIs
export const reportsAPI = {
  getDre: (period) => api.get('/reports/dre', { params: { period } }),
  getDreMonthly: (months = 6) => api.get('/reports/dre/monthly', { params: { months } }),
  getAging: () => api.get('/reports/aging'),
  exportDre: (period, format = 'xlsx') => api.get(`/reports/export/dre${format === 'pdf' ? '/pdf' : ''}`, { 
    params: format !== 'pdf' ? { period, format } : { period },
    responseType: 'blob'
  }),
  exportAging: (format = 'xlsx') => api.get('/reports/export/aging', { 
    params: { format },
    responseType: 'blob'
  }),
};

// Purchases APIs
export const purchasesAPI = {
  list: (params) => api.get('/purchases', { params }),
  get: (id) => api.get(`/purchases/${id}`),
  create: (data) => api.post('/purchases', data),
  update: (id, data) => api.put(`/purchases/${id}`, data),
  delete: (id) => api.delete(`/purchases/${id}`),
  receive: (id, params) => api.post(`/purchases/${id}/receive`, null, { params }),
  getStats: (params) => api.get('/purchases/stats/summary', { params }),
};

// Credit Cards APIs
export const creditCardsAPI = {
  list: (params) => api.get('/credit-cards', { params }),
  get: (id) => api.get(`/credit-cards/${id}`),
  create: (data) => api.post('/credit-cards', data),
  update: (id, data) => api.put(`/credit-cards/${id}`, data),
  delete: (id) => api.delete(`/credit-cards/${id}`),
  listExpenses: (cardId, params) => api.get(`/credit-cards/${cardId}/expenses`, { params }),
  createExpense: (data) => api.post('/credit-cards/expenses', data),
  deleteExpense: (expenseId) => api.delete(`/credit-cards/expenses/${expenseId}`),
  listInvoices: (cardId) => api.get(`/credit-cards/${cardId}/invoices`),
  payInvoice: (cardId, invoiceMonth, params) => api.post(`/credit-cards/${cardId}/invoices/${invoiceMonth}/pay`, null, { params }),
  getStats: () => api.get('/credit-cards/stats/summary'),
};

// Sales/Quotes APIs
export const salesAPI = {
  list: (params) => api.get('/sales', { params }),
  get: (id) => api.get(`/sales/${id}`),
  create: (data) => api.post('/sales', data),
  update: (id, data) => api.put(`/sales/${id}`, data),
  delete: (id) => api.delete(`/sales/${id}`),
  invoice: (id, params) => api.post(`/sales/${id}/invoice`, null, { params }),
  partialInvoice: (id, data) => api.post(`/sales/${id}/partial-invoice`, data),
  cancelInvoice: (id, data) => api.post(`/sales/${id}/cancel-invoice`, data),
  cancel: (id, data) => api.post(`/sales/${id}/cancel`, data),
  cancelItems: (id, data) => api.post(`/sales/${id}/cancel-items`, data),
  convert: (id) => api.post(`/sales/${id}/convert`),
  duplicate: (id) => api.post(`/sales/${id}/duplicate`),
  reopen: (id) => api.post(`/sales/${id}/reopen`),
  deliver: (id) => api.post(`/sales/${id}/deliver`),
  generateNF: (id) => api.post(`/sales/${id}/generate-nf`),
  getStats: (params) => api.get('/sales/stats/summary', { params }),
};

export const settingsAPI = {
  getStockPolicy: () => api.get('/settings/stock-policy'),
  updateStockPolicy: (data) => api.put('/settings/stock-policy', data),
};

// Contracts APIs
export const contractsAPI = {
  list: (params) => api.get('/contracts', { params }),
  get: (id) => api.get(`/contracts/${id}`),
  create: (data) => api.post('/contracts', data),
  update: (id, data) => api.put(`/contracts/${id}`, data),
  delete: (id) => api.delete(`/contracts/${id}`),
  generate: (id, numInstallments = 1) => api.post(`/contracts/${id}/generate`, null, { 
    params: { num_installments: numInstallments } 
  }),
  getStats: () => api.get('/contracts/stats/summary'),
};

// Email Notifications APIs
export const emailAPI = {
  getStatus: () => api.get('/email/status'),
  sendTest: (data) => api.post('/email/test', data),
  sendNotification: (data) => api.post('/email/send-notification', data),
  getLogs: (limit = 20) => api.get('/email/logs', { params: { limit } }),
};

// Company (Multi-tenancy) APIs
export const companyAPI = {
  list: () => api.get('/companies'),
  get: (id) => api.get(`/companies/${id}`),
  create: (data) => api.post('/companies', data),
  update: (id, data) => api.put(`/companies/${id}`, data),
  delete: (id) => api.delete(`/companies/${id}`),
  switch: (id) => api.post(`/companies/${id}/switch`),
  leave: (id) => api.post(`/companies/${id}/leave`),
  getMembers: (id) => api.get(`/companies/${id}/members`),
  // Invitations
  invite: (id, data) => api.post(`/companies/${id}/invite`, data),
  getInvites: (id) => api.get(`/companies/${id}/invites`),
  cancelInvite: (companyId, inviteId) => api.delete(`/companies/${companyId}/invites/${inviteId}`),
};

// Invitations APIs
export const inviteAPI = {
  getByToken: (token) => api.get(`/invites/${token}`),
  accept: (token) => api.post(`/invites/${token}/accept`),
  getMyInvites: () => api.get('/my-invites'),
};

// Dashboard Layout APIs
export const dashboardLayoutAPI = {
  get: () => api.get('/dashboard/layout'),
  save: (layout) => api.put('/dashboard/layout', { layout }),
  reset: () => api.post('/dashboard/layout/reset'),
};

// Audit Logs APIs
export const auditLogAPI = {
  list: (params) => api.get('/audit-logs', { params }),
  getActions: () => api.get('/audit-logs/actions'),
};

// Reconciliation Rules APIs
export const reconciliationRulesAPI = {
  list: (params) => api.get('/reconciliation-rules', { params }),
  get: (id) => api.get(`/reconciliation-rules/${id}`),
  create: (data) => api.post('/reconciliation-rules', data),
  update: (id, data) => api.put(`/reconciliation-rules/${id}`, data),
  delete: (id) => api.delete(`/reconciliation-rules/${id}`),
  toggle: (id) => api.post(`/reconciliation-rules/${id}/toggle`),
  apply: (bankTransactionId) => api.post('/reconciliation-rules/apply', null, { params: { bank_transaction_id: bankTransactionId } }),
  test: (id, limit = 20) => api.post(`/reconciliation-rules/${id}/test`, null, { params: { limit } }),
  duplicate: (id) => api.post(`/reconciliation-rules/${id}/duplicate`),
};

// Enhanced Reconciliation APIs
export const enhancedReconciliationAPI = {
  getEnhancedSuggestions: (bankTxId) => api.get('/reconciliation/enhanced-suggestions', { params: { bank_transaction_id: bankTxId } }),
  enhancedReconcile: (data, createRule = false, ruleName = null) => {
    const params = { create_rule: createRule };
    if (ruleName) params.rule_name = ruleName;
    return api.post('/reconciliation/enhanced-reconcile', data, { params });
  },
  bulkReconcile: (data) => api.post('/reconciliation/bulk-reconcile', data),
  autoReconcileAll: (accountId = null, minConfidence = 90) => 
    api.post('/reconciliation/auto-reconcile-all', null, { params: { account_id: accountId, min_confidence: minConfidence } }),
  getEnhancedStats: (accountId = null, periodDays = 30) => 
    api.get('/reconciliation/enhanced-stats', { params: { account_id: accountId, period_days: periodDays } }),
  createRuleFromTransaction: (data) => api.post('/reconciliation/create-rule-from-transaction', data),
  getLogs: (params) => api.get('/reconciliation/logs', { params }),
  ignore: (bankTxId, reason) => api.post(`/reconciliation/ignore/${bankTxId}`, null, { params: { reason } }),
};

// Email Scheduling APIs
export const emailSchedulesAPI = {
  list: () => api.get('/email/schedules'),
  create: (data) => api.post('/email/schedules', data),
  update: (id, data) => api.put(`/email/schedules/${id}`, data),
  delete: (id) => api.delete(`/email/schedules/${id}`),
  toggle: (id) => api.post(`/email/schedules/${id}/toggle`),
  run: (id) => api.post(`/email/schedules/${id}/run`),
};

// Open Finance APIs
export const openFinanceAPI = {
  listBanks: () => api.get('/openfinance/banks'),
  listConsents: () => api.get('/openfinance/consents'),
  initiateConsent: (bankCode) => api.post(`/openfinance/consent/initiate?bank_code=${bankCode}`),
  authorizeConsent: (consentId) => api.post(`/openfinance/consent/${consentId}/authorize`),
  revokeConsent: (consentId) => api.delete(`/openfinance/consent/${consentId}`),
  getAccounts: (consentId) => api.get(`/openfinance/consent/${consentId}/accounts`),
  getTransactions: (consentId, accountId) => api.get(`/openfinance/consent/${consentId}/transactions`, { params: { account_id: accountId } }),
  syncTransactions: (consentId, accountId, targetAccountId) => api.post(`/openfinance/consent/${consentId}/sync`, null, { params: { account_id: accountId, target_account_id: targetAccountId } }),
};

// Credit Card Invoice Import APIs
export const creditCardInvoiceAPI = {
  parseInvoice: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/creditcards/parse-invoice', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
  },
  importInvoice: (cardId, file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post(`/creditcards/${cardId}/import-invoice`, formData, { headers: { 'Content-Type': 'multipart/form-data' } });
  },
};

// Boleto APIs
export const boletoAPI = {
  parseLine: (line) => api.post(`/boletos/parse-line?line=${encodeURIComponent(line)}`),
  parsePdf: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/boletos/parse-pdf', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
  },
  import: (data) => {
    const formData = new FormData();
    if (data.file) formData.append('file', data.file);
    const params = new URLSearchParams();
    if (data.line) params.append('line', data.line);
    if (data.description) params.append('description', data.description);
    if (data.person_id) params.append('person_id', data.person_id);
    if (data.category_id) params.append('category_id', data.category_id);
    if (data.cost_center_id) params.append('cost_center_id', data.cost_center_id);
    return api.post(`/boletos/import?${params.toString()}`, formData, { headers: { 'Content-Type': 'multipart/form-data' } });
  },
};

// 2FA APIs
export const twoFactorAPI = {
  setup: () => api.post('/auth/2fa/setup'),
  verify: (code) => api.post('/auth/2fa/verify', { code }),
  disable: (code) => api.post('/auth/2fa/disable', { code }),
  getBackupCodes: () => api.get('/auth/2fa/backup-codes'),
};

// RBAC APIs
export const rbacAPI = {
  listRoles: () => api.get('/rbac/roles'),
  getRoleDetails: (roleId) => api.get(`/rbac/roles/${roleId}`),
  updateMemberRole: (companyId, userId, role) => api.put(`/companies/${companyId}/members/${userId}/role?role=${role}`),
};

// Attachments APIs
export const attachmentsAPI = {
  upload: (file, entityType, entityId) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post(`/attachments/upload?entity_type=${entityType}&entity_id=${entityId}`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  list: (entityType, entityId) => api.get(`/attachments/${entityType}/${entityId}`),
  download: (attachmentId) => api.get(`/attachments/${attachmentId}/download`, { responseType: 'blob' }),
  delete: (attachmentId) => api.delete(`/attachments/${attachmentId}`),
};

// Backup APIs
export const backupAPI = {
  create: () => api.post('/backup/create'),
  list: () => api.get('/backup/list'),
  download: (backupId) => api.get(`/backup/${backupId}/download`, { responseType: 'blob' }),
  restore: (backupId, mode = 'merge') => api.post(`/backup/${backupId}/restore?mode=${mode}`),
  delete: (backupId) => api.delete(`/backup/${backupId}`),
};

// Global Search API
export const searchAPI = {
  search: (query, limit = 5) => api.get('/search', { params: { q: query, limit } }),
};

// Budget APIs (Orçamento Empresarial)
export const budgetAPI = {
  list: (params) => api.get('/budgets', { params }),
  create: (data) => api.post('/budgets', data),
  update: (id, data) => api.put(`/budgets/${id}`, data),
  delete: (id) => api.delete(`/budgets/${id}`),
  getSummary: (year) => api.get('/budgets/summary', { params: { year } }),
  getAlerts: (year) => api.get('/budgets/alerts', { params: { year } }),
  duplicateMonth: (sourceYear, sourceMonth, targetYear, targetMonth) => 
    api.post('/budgets/duplicate-month', null, { 
      params: { source_year: sourceYear, source_month: sourceMonth, target_year: targetYear, target_month: targetMonth } 
    }),
  close: (id) => api.post(`/budgets/${id}/close`),
};

// Expense Allocation APIs (Rateio de Despesas)
export const allocationAPI = {
  list: (params) => api.get('/allocations', { params }),
  create: (data) => api.post('/allocations', data),
  delete: (id) => api.delete(`/allocations/${id}`),
  getByCostCenter: (params) => api.get('/allocations/by-cost-center', { params }),
};

// Accounting Integration APIs (Integração Contábil)
export const accountingAPI = {
  getSummary: (startDate, endDate) => api.get('/accounting/summary', { 
    params: { start_date: startDate, end_date: endDate } 
  }),
  exportTransactions: (params) => api.get('/accounting/export/transactions', { 
    params: {
      start_date: params.start_date,
      end_date: params.end_date,
      format: params.format,
      include_payables: params.include_payables,
      include_receivables: params.include_receivables
    },
    responseType: 'blob'
  }),
  exportChartOfAccounts: (format) => api.get('/accounting/export/chart-of-accounts', { 
    params: { format },
    responseType: 'blob'
  }),
  exportSped: (params) => api.get('/accounting/export/sped', { 
    params: {
      start_date: params.start_date,
      end_date: params.end_date,
      tipo: params.tipo
    },
    responseType: 'blob'
  }),
  getExportFormats: () => api.get('/accounting/export-formats'),
};

// API Keys & Webhooks
export const apiKeysAPI = {
  list: () => api.get('/api-keys'),
  create: (data) => api.post('/api-keys', data),
  revoke: (id) => api.delete(`/api-keys/${id}`),
};

export const webhooksAPI = {
  list: () => api.get('/webhooks'),
  create: (data) => api.post('/webhooks', data),
  update: (id, data) => api.put(`/webhooks/${id}`, data),
  delete: (id) => api.delete(`/webhooks/${id}`),
  test: (id) => api.post(`/webhooks/${id}/test`),
  getEvents: () => api.get('/webhooks/events'),
  getLogs: (params) => api.get('/webhooks/logs', { params }),
};

// Invoices (Notas Fiscais)
export const invoicesAPI = {
  list: (params) => api.get('/invoices', { params }),
  get: (id) => api.get(`/invoices/${id}`),
  create: (data) => api.post('/invoices', data),
  authorize: (id) => api.post(`/invoices/${id}/authorize`),
  cancel: (id, reason) => api.post(`/invoices/${id}/cancel`, null, { params: { reason } }),
  delete: (id) => api.delete(`/invoices/${id}`),
  getSummary: (params) => api.get('/invoices/stats/summary', { params }),
};

// Products & Inventory APIs
export const productsAPI = {
  list: (params) => api.get('/products', { params }),
  get: (id) => api.get(`/products/${id}`),
  create: (data) => api.post('/products', data),
  update: (id, data) => api.put(`/products/${id}`, data),
  inactivate: (id) => api.post(`/products/${id}/inactivate`),
  reactivate: (id) => api.post(`/products/${id}/reactivate`),
  duplicate: (id) => api.post(`/products/${id}/duplicate`),
  // Suppliers
  addSupplier: (id, data) => api.post(`/products/${id}/suppliers`, data),
  removeSupplier: (productId, linkId) => api.delete(`/products/${productId}/suppliers/${linkId}`),
  // Stock adjustments
  adjustStock: (data) => api.post('/inventory/adjustments', data),
  // Units and brands
  getUnits: () => api.get('/units'),
  getBrands: () => api.get('/brands'),
  createBrand: (name) => api.post('/brands', null, { params: { name } }),
  deleteBrand: (id) => api.delete(`/brands/${id}`),
};

// Inventory APIs
export const inventoryAPI = {
  listMovements: (params) => api.get('/inventory/movements', { params }),
  listBalances: (params) => api.get('/inventory/balances', { params }),
};

// Services APIs
export const servicesAPI = {
  list: (params) => api.get('/services', { params }),
  get: (id) => api.get(`/services/${id}`),
  create: (data) => api.post('/services', data),
  update: (id, data) => api.put(`/services/${id}`, data),
  inactivate: (id) => api.post(`/services/${id}/inactivate`),
  reactivate: (id) => api.post(`/services/${id}/reactivate`),
  duplicate: (id) => api.post(`/services/${id}/duplicate`),
  // Categories
  getCategories: () => api.get('/service-categories'),
  createCategory: (data) => api.post('/service-categories', data),
  deleteCategory: (id) => api.delete(`/service-categories/${id}`),
};

// Service Orders (OS) APIs
export const serviceOrdersAPI = {
  list: (params) => api.get('/service-orders', { params }),
  get: (id) => api.get(`/service-orders/${id}`),
  create: (data) => api.post('/service-orders', data),
  update: (id, data) => api.put(`/service-orders/${id}`, data),
  start: (id) => api.post(`/service-orders/${id}/start`),
  complete: (id, notes) => api.post(`/service-orders/${id}/complete`, null, { params: { completion_notes: notes } }),
  cancel: (id, reason) => api.post(`/service-orders/${id}/cancel`, null, { params: { reason } }),
  bill: (id, accountId, dueDate, amount = null) => api.post(`/service-orders/${id}/bill`, null, { 
    params: { account_id: accountId, due_date: dueDate, ...(amount !== null ? { amount } : {}) } 
  }),
  getBillingHistory: (id) => api.get(`/service-orders/${id}/billing-history`),
  getStats: () => api.get('/service-orders/stats/summary'),
};

// Integrations Hub APIs
export const integrationsAPI = {
  list: () => api.get('/integrations'),
  get: (id) => api.get(`/integrations/${id}`),
  updateStatus: (id, status) => api.put(`/integrations/${id}/status`, null, { params: { status } }),
  test: (id) => api.post(`/integrations/${id}/test`),
  getLogs: (params) => api.get('/integrations/logs', { params }),
  getApiKey: () => api.get('/integrations/api-key'),
};

// Automations Engine APIs
export const automationsAPI = {
  list: () => api.get('/automations'),
  get: (id) => api.get(`/automations/${id}`),
  create: (data) => api.post('/automations', data),
  update: (id, data) => api.put(`/automations/${id}`, data),
  delete: (id) => api.delete(`/automations/${id}`),
  toggle: (id) => api.post(`/automations/${id}/toggle`),
  duplicate: (id) => api.post(`/automations/${id}/duplicate`),
  testRun: (id) => api.post(`/automations/${id}/test`),
  getLogs: (params) => api.get('/automations/logs', { params }),
};

// CEP Lookup
export const cepAPI = {
  lookup: (cep) => api.get(`/cep/${cep}`),
};

// Similar entities (fuzzy search)
export const similarEntitiesAPI = {
  find: (params) => api.get('/entities/similar', { params }),
};

export default api;
